import BigNumber from 'bignumber.js';
import { GrfqLeg } from '#/unified-rfqs/entities/grfq/domain';
import formatDecimal, {
  isValidDecimal,
  parseFormattedFloat,
  parseInputNumber,
  removeTrailingZeroes,
} from '@paradigm/utils/formatDecimal';
import { TwoWayValue } from '#/unified-rfqs/ui/grfq/quote-builder/form-reducer';
import { getProduct } from '#/products/domain';
import { Side } from '#/unified-rfqs/entities/shared';
import { hasNonIntegerRatio } from '#/unified-rfqs/ui/rfqs/legs-functions';
import { roundPrecisionBySide } from '#/unified-rfqs/utils/rfq-price-functions';

interface PriceViewArgs {
  readonly price: string;
  readonly side: Side;
  readonly legPricingEnabled: boolean;
  readonly legsPrice: readonly TwoWayValue[];
  readonly legs: readonly GrfqLeg[];
}

export default function priceView(args: PriceViewArgs) {
  const precision = processPricePrecision(args.legs, args.legPricingEnabled);
  const value = processCustomLegsPrice(args, precision, args.side);
  return { precision, value };
}

function processPricePrecision(
  legs: readonly GrfqLeg[],
  legPricingEnabled: boolean,
) {
  const [firstLeg] = legs;
  const { pricePrecision, ratioPrecision } = getProduct(firstLeg!.product_code);
  if (hasNonIntegerRatio(legs)) {
    return pricePrecision + 1;
  }
  if (!legPricingEnabled) return pricePrecision;

  return pricePrecision + ratioPrecision;
}

function processCustomLegsPrice(
  args: PriceViewArgs,
  precision: number,
  side: Side,
): string {
  if (!args.legPricingEnabled) {
    return formatPrice(args.price, precision, side);
  }

  let bid = BigNumber(0);
  let ask = BigNumber(0);
  let hasBidPrices = false;
  let hasAskPrices = false;

  for (const [index, leg] of args.legs.entries()) {
    const legPrices = args.legsPrice[index];

    const isBuyLeg = leg.side === 'BUY';
    const isSellLeg = leg.side === 'SELL';

    const bidPrice = legPrices?.[isSellLeg ? 1 : 0] ?? '';
    if (bidPrice !== '') hasBidPrices = true;
    bid = bid.plus(normalizePrice(bidPrice, leg));

    const askPrice = legPrices?.[isBuyLeg ? 1 : 0] ?? '';
    if (askPrice !== '') hasAskPrices = true;
    ask = ask.plus(normalizePrice(askPrice, leg));
  }
  if (side === 'BUY') {
    return hasBidPrices ? formatPrice(bid, precision, 'BUY') : '';
  }
  return hasAskPrices ? formatPrice(ask, precision, 'SELL') : '';
}

const formatPrice = (
  decimal: BigNumber | string,
  precision: number,
  side: Side,
) => {
  let bigNum = BigNumber(decimal);
  if (typeof decimal === 'string') {
    if (!isValidDecimal(decimal)) return '';
    bigNum = BigNumber(parseFormattedFloat(decimal));
  }

  const roundedPrice = roundPrecisionBySide(bigNum.toString(), side, precision);

  return formatDecimal(removeTrailingZeroes(roundedPrice));
};

function normalizePrice(price: string, { side, ratio }: GrfqLeg) {
  const isBuyLeg = side === 'BUY';
  const parsedRatio = parseFormattedFloat(ratio) * (isBuyLeg ? 1 : -1);
  const inputPriceParsed = parseInputNumber(price);
  if (inputPriceParsed === null) return 0;

  return BigNumber(inputPriceParsed).times(parsedRatio);
}
